import { Capacitor } from '@capacitor/core';
import { API_BASE } from '../config';

/**
 * Обновление приложения без переустановки.
 *
 * Интерфейс целиком лежит внутри APK, поэтому раньше любая правка означала
 * «скачайте и поставьте заново» для всех тридцати человек. Здесь приложение
 * забирает с сервера новую сборку интерфейса и подменяет её у себя.
 *
 * Обновление запускает человек из меню, а не оно само: подменять интерфейс
 * без спроса посреди переписки — худшее, что можно сделать с мессенджером.
 *
 * Нативную часть (плагины, настройки Android) так заменить нельзя — для неё
 * по-прежнему нужен новый APK. На практике это редкость: за всё время таких
 * правок было две из двадцати с лишним.
 */

/** Версия этой сборки. Подставляется при сборке, см. vite.config.ts. */
export const CURRENT_VERSION: string = __APP_VERSION__;

/** Откуда берём сведения о свежей сборке. Тот же домен, что и у API. */
const MANIFEST_URL = `${API_BASE}/updates/version.json`;

export interface Available {
  version: string;
  url: string;
  /** Размер архива в байтах — показываем, чтобы не качать вслепую по мобильной сети. */
  size: number;
  /**
   * SHA-256 архива. Без неё плагин отказывается скачивать вообще: проверка
   * целостности для подменяемого на лету интерфейса обязательна, иначе
   * испорченный по дороге архив встал бы как рабочая сборка.
   */
  sha256: string;
}

export type CheckResult =
  | { status: 'unsupported' }
  | { status: 'current' }
  | { status: 'available'; update: Available }
  | { status: 'error'; message: string };

/** Обновлять можно только приложение: браузер обновляется сам при перезагрузке. */
export function updatesSupported(): boolean {
  return Capacitor.isNativePlatform();
}

export async function checkForUpdate(): Promise<CheckResult> {
  if (!updatesSupported()) {
    return { status: 'unsupported' };
  }
  try {
    // no-store: иначе телефон отдаёт закэшированный ответ и «обновлений нет»
    // даже когда они есть
    const response = await fetch(MANIFEST_URL, { cache: 'no-store' });
    if (!response.ok) {
      return { status: 'error', message: `Сервер ответил ${response.status}` };
    }
    const manifest = (await response.json()) as Partial<Available>;
    if (!manifest.version || !manifest.url || !manifest.sha256) {
      return { status: 'error', message: 'Сервер прислал непонятный ответ' };
    }
    if (manifest.version === CURRENT_VERSION) {
      return { status: 'current' };
    }
    return {
      status: 'available',
      update: {
        version: manifest.version,
        url: manifest.url,
        size: manifest.size ?? 0,
        sha256: manifest.sha256,
      },
    };
  } catch {
    return { status: 'error', message: 'Нет связи с сервером' };
  }
}

/**
 * Скачивает сборку и переключается на неё. Приложение перезапускается само.
 *
 * Плагин подгружается динамически: в браузерную сборку его код попадать
 * не должен, там он бесполезен.
 */
export async function applyUpdate(update: Available): Promise<void> {
  const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
  const bundle = await CapacitorUpdater.download({
    url: update.url,
    version: update.version,
    // Обязательна: без неё плагин отказывает ещё до обращения к сети,
    // а сообщение выглядит как «не удалось скачать» и уводит в сторону
    checksum: update.sha256,
  });
  await CapacitorUpdater.set({ id: bundle.id });
}

/**
 * Сообщает, что приложение успешно запустилось.
 *
 * Без этого плагин считает обновление неудачным и при следующем запуске
 * возвращает встроенную сборку. Это и есть защита от «кирпича»: сломанная
 * сборка откатится сама, без похода к каждому владельцу телефона.
 */
export async function confirmLaunch(): Promise<void> {
  if (!updatesSupported()) return;
  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');
    await CapacitorUpdater.notifyAppReady();
  } catch {
    // Старая сборка без плагина — ничего страшного
  }
}
